/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    CrfFormByCodesProvider,
    FormBreadcrumbs,
    FormDefinitionContext,
    FormSubmitButton,
    GroupedField,
    ICrfConditionParameters,
    ICrfFormProps,
    InstitutionByCodeProvider,
    InstitutionContext,
    InstitutionsContext,
    MasterGroupContext,
    PageLayout,
    Password,
    PatientContext,
    PatientDetailsByStudyNumberProvider,
    PatientsContext,
    RouteLoading,
    Select,
    Text,
    useSnackbar
} from '@ngt/opms';

import { ConditionMode, ValidateOn, useFormActions, useScopedField } from '@ngt/forms-core';

import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';

import { FC, useCallback, useContext, useEffect, useMemo } from 'react';

import { AlertTitle, Box, Button, Dialog, DialogContentText, Typography, styled } from '@mui/material';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import ScreeningDialogContext from '../../../contexts/ScreeningContext';
import { AppSettings } from '../../../AppSettings';


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const ButtonGroup = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2,2),
    gap: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end'
}))

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
    padding: theme.spacing(0,2,2)
}))

const StyledDialogTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    fontSize: '1.25rem',
    fontWeight: 500
}));

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Interfaces
 * ----------------------------------------------------------------------------------
 */
interface IScreeningFormProps { 
}

type OmitCalculatedTypes<T> = Omit<T, 'onValidate' | 'allowSubmit' | 'initialValues' | 'lookups' | 'labels'>

export interface ICrfDialogFormProps<Type extends Dtos.IForm> extends OmitCalculatedTypes<ICrfFormProps<Type>> {
    dialogTitle?: string;
    hideButtons?: boolean;
    hideSubmit?: boolean;
    hideCancel?: boolean;
}

/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */

const screenFail = ({ formState }: ICrfConditionParameters<Dtos.ScreeningForm>) => formState?.value?.outcome === Dtos.LookupScreeningOutcomeType.Fail;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const CrfDialogForm = <Type extends Dtos.IForm>({
    dialogTitle,
    children,
    ...formProps
}: ICrfDialogFormProps<Type>) => {
    return (
        <CrfForm
            {...formProps}
            debug={AppSettings.debug}
            disableHeading
        >
            <StyledDialogTitle
                color="secondary"
            >
                {dialogTitle ?? formProps.entity}
            </StyledDialogTitle>
            
            {children}
        </CrfForm>
    );
}

const ScreeningOutcomeFields: FC = () => {

    const { screeningOutcome } = useContext(ScreeningDialogContext);

    const { setFieldValue } = useFormActions();

    useEffect(() => {
        if(screeningOutcome) {
            setFieldValue(`outcome`, screeningOutcome, true, true, true, false);
        }
    }, [screeningOutcome, setFieldValue]);

    const { state: { value: outcomeValue } } = useScopedField(`outcome`, { value: true });

    return <>
        {
            (screeningOutcome ?? outcomeValue) === Dtos.LookupScreeningOutcomeType.Proceed && 
            <>
                <StyledDialogContentText>
                    Please provide the participant's email address.
                    <br/><br/>
                    Proceeding to consent will send an email notification to the participant. This email contains the link to the information page.
                </StyledDialogContentText>
                <GroupedField
                    propertyPath="participantEmail"
                    component={Text}
                    FieldGridProps={{
                        size: {
                            xl: 12,
                            lg: 12,
                            md: 12,
                            sm: 12,
                            xs: 12
                        }
                    }}
                    GroupedFieldErrorFlagProps={{
                        showErrorFlag: (errors, touched, _focused, _validating, isAlwaysHidden, _isAlwaysShown) => {
                            if (isAlwaysHidden) {
                                return false;
                            }

                            // if (isAlwaysShown) {
                            //     return true;
                            // }

                            if (touched && errors && errors.length > 0) {
                                return true;
                            }
                            
                            return false;
                        }
                    }}
                    GroupedFieldErrorDisplayProps={{
                        showError: (name, _error, touched, _errors, _validating, _isAlwaysShown) => {
                            return touched[name] ? true : false;
                        }
                    }}
                />
            </>
        }
        {
            (screeningOutcome ?? outcomeValue) === Dtos.LookupScreeningOutcomeType.Fail && (
                <GroupedField
                    propertyPath="screenFailReason"
                    component={Select}
                    LabelProps={{
                        label: screeningOutcome ? "Please provide the reason for failing the screening" as any : undefined
                    }}
                    FieldGridProps={{
                        size: {
                            xl: 12,
                            lg: 12,
                            md: 12,
                            sm: 12,
                            xs: 12
                        }
                    }}
                    GroupedFieldErrorFlagProps={{
                        showErrorFlag: (errors, touched, _focused, _validating, isAlwaysHidden, _isAlwaysShown) => {
                            if (isAlwaysHidden) {
                                return false;
                            }

                            // if (isAlwaysShown) {
                            //     return true;
                            // }

                            if (touched && errors && errors.length > 0) {
                                return true;
                            }
                            
                            return false;
                        }
                    }}
                    GroupedFieldErrorDisplayProps={{
                        showError: (name, _error, touched, _errors, _validating, _isAlwaysShown) => {
                            return touched[name] ? true : false;
                        }
                    }}
                />
            )
        }
    </>
}

const ScreeningDialogForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institutions } = useContext(InstitutionsContext);
    const { data: patients, refresh: refreshPatients } = useContext(PatientsContext);
    const { dialogOpen, patientStudyNumber, institutionCode, screeningOutcome, setDialogOpen, setPatientStudyNumber, setScreeningOutcome } = useContext(ScreeningDialogContext);

    const institution = useMemo(() => {
        return institutions?.find(x => x.code === institutionCode)
    }, [institutionCode, institutions])

    const patient = useMemo(() => {
        return patients?.find(p => p.studyNumber === patientStudyNumber) as Dtos.Patient;
    }, [patients, patientStudyNumber]);

    const {
        permissions: [
            canUpdateCrf, 
            canAdministerOpms
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate,
            Permission.OpmsAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const onDialogClose = useCallback(() => {
        setDialogOpen(false);
        setPatientStudyNumber(null);
        setScreeningOutcome(null);
    }, [setDialogOpen, setPatientStudyNumber, setScreeningOutcome]);

    const { enqueueSnackbar } = useSnackbar();

    const afterDialogSave = useCallback(() => {
        refreshPatients();
        onDialogClose();

        if(screeningOutcome === Dtos.LookupScreeningOutcomeType.Proceed)
        {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Information Sent
                    </AlertTitle>
                    The information email was successfully sent to the participant.
                </>,
                { variant: 'success' }
            );
        }

    }, [onDialogClose, refreshPatients, screeningOutcome, history, patient, institution, enqueueSnackbar]);

    const dialogTitle = useMemo(() => {
        return `Screening ${patient ? '(Participant ' + patient.studyNumber + ')' : ''}`

    }, [ patient]);

    const form = (
        <CrfFormByCodesProvider
            institutionCode={patient?.institutionCode ?? institutionCode}
            patientStudyNumber={patient?.studyNumber}
            eventDefinitionIdentifier="screening"
            eventRepeat={1}
            formDefinitionIdentifier="screening-form"
            formRepeat={1}
            createEvent={true}
            createPatient={true}
            createForm={true}
            loadBeforeMount
            LoadingComponent={RouteLoading}
        >
            <CrfDialogForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                hideButtons={!!screeningOutcome}
                hideSubmitAndReturn={true}
                onCancel={onDialogClose}
                afterFormSave={afterDialogSave}
                dialogTitle={dialogTitle}
            >
                {
                    !screeningOutcome && <>
                        <GroupedField
                            propertyPath="initials"
                            component={(canAdministerOpms && patientStudyNumber) ? Password : Text}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                            disabled={!!canAdministerOpms && !!patientStudyNumber}
                        />
                        <GroupedField
                            propertyPath="yearOfBirth"
                            component={(canAdministerOpms && patientStudyNumber) ? Password : Text}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                            disabled={!!canAdministerOpms && !!patientStudyNumber}
                        />
                    </>
                }
                <ScreeningOutcomeFields />
                {
                    screeningOutcome && <>
                        <ButtonGroup>
                            <Button
                                variant="text"
                                color="secondary"
                                onClick={onDialogClose}
                            >
                                Cancel
                            </Button>
                            {
                                screeningOutcome === Dtos.LookupScreeningOutcomeType.Proceed && <FormSubmitButton
                                    variant="contained"
                                    color="primary"
                                >
                                    Proceed to Consent
                                </FormSubmitButton>
                            }
                            {
                                screeningOutcome === Dtos.LookupScreeningOutcomeType.Fail && <FormSubmitButton
                                    variant="contained"
                                    color="primary"
                                >
                                    Screen Fail
                                </FormSubmitButton>
                            }
                        </ButtonGroup>
                    </>
                }
            </CrfDialogForm>
        </CrfFormByCodesProvider>
    )


    return (
        <>
            {
                (patient || institutionCode) && <Dialog
                    open={dialogOpen}
                    onClose={onDialogClose}
                    aria-labelledby="form-dialog-title"
                    fullWidth={true}
                    scroll="body"
                    maxWidth="sm"
                >
                {
                    (permissionsLoading) ?  
                        <RouteLoading /> :
                        patient ?
                            <PatientDetailsByStudyNumberProvider
                                patientStudyNumber={patient?.studyNumber}
                                loadBeforeMount
                                LoadingComponent={RouteLoading}
                            >
                                {form}
                            </PatientDetailsByStudyNumberProvider> :
                            <InstitutionByCodeProvider 
                                institutionCode={institutionCode ?? undefined}
                                loadBeforeMount
                                LoadingComponent={RouteLoading}
                            >
                                {form}
                            </InstitutionByCodeProvider>
                }
                </Dialog>
            }
        </>
    )
}

const ScreeningCrfForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf, 
            canAdministerOpms
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate,
            Permission.OpmsAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return <>
        <CrfForm
            validateOn={ValidateOn.onChange}
            readOnly={!canUpdateCrf}
            debug={AppSettings?.debug}
        >
            <GroupedField
                propertyPath="initials"
                component={(canAdministerOpms && patient?.id) ? Password : Text}
                disabled={!!canAdministerOpms && !!patient?.id}
            />
            <GroupedField
                propertyPath="yearOfBirth"
                component={(canAdministerOpms && patient?.id) ? Password : Text}
                disabled={!!canAdministerOpms && !!patient?.id}
            />
            <CrfCondition
                mode={ConditionMode.Show}
                condition={screenFail}
                subscription={{ value: true }}
            >
                <GroupedField
                    propertyPath="screenFailReason"
                    component={Select}
                />
            </CrfCondition>
        </CrfForm>
    </>
}

const ScreeningForm: FC<IScreeningFormProps> = () => {
    //const classes = useStyles();
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);

    return <>
        {
            patient && formDefinition && (
                <PageLayout
                    breadcrumbs={<FormBreadcrumbs />}
                >
                    <ScreeningCrfForm />
                </PageLayout>
            )   
        }

        <ScreeningDialogForm />
    </>
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default ScreeningForm;
